import { useEffect, useState } from 'react';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import SlotImage from '@/components/base/SlotImage';
import { useLiveStreamSettings } from '@/hooks/useLiveStreamSettings';
import { supabase } from '@/lib/supabase';

export default function LivePage() {
  const { data: live, loading } = useLiveStreamSettings();
  const isLive = live?.is_live ?? false;

  // Prayer form state
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [request, setRequest] = useState('');
  const [anonymous, setAnonymous] = useState(false);
  const [honeypot, setHoneypot] = useState('');
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [msg, setMsg] = useState('');
  const MAX_LENGTH = 500;

  useEffect(() => {
    document.title = 'Watch Live — TGMCI';
    window.scrollTo(0, 0);
  }, []);

  const handlePrayer = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (honeypot.trim()) { setStatus('success'); setMsg('Your prayer request has been received.'); return; }
    if (!request.trim()) return;
    if (request.length > MAX_LENGTH) return;
    setStatus('loading');

    const { error: insertErr } = await supabase.from('prayer_requests').insert({
      name: anonymous ? null : name,
      email: anonymous ? null : email,
      request,
      is_anonymous: anonymous,
    });

    if (insertErr) {
      setStatus('error');
      setMsg('Something went wrong. Please try again.');
    } else {
      setStatus('success');
      setMsg('Your prayer request has been received. Our team will be praying for you.');
      setName(''); setEmail(''); setRequest(''); setAnonymous(false);
    }
  };

  return (
    <div className="min-h-screen bg-background-50">
      <Header />
      <main>
        {/* Hero */}
        <section className="relative pt-40 pb-16 overflow-hidden bg-background-950 text-center">
          <div className="absolute inset-0">
            <SlotImage
              slot="live_hero"
              fallback="https://storage.helloreaddy.io/project_files/e45ec48e-345c-4840-a547-bcc42428da39/1ab415ec-9dc5-4ff9-83f5-083be47621da_compressed_WhatsApp-Image-2026-09-12-at-15.29.02-1.webp"
              alt="Live service background"
              className="w-full h-full object-cover object-top opacity-15"
            />
          </div>
          <div className="relative z-10 max-w-[800px] mx-auto px-4">
            <div className="inline-flex items-center gap-2 mb-4">
              {isLive ? (
                <span className="inline-flex items-center gap-2 bg-red-500 text-white text-xs font-label font-semibold tracking-widest uppercase px-4 py-2 rounded-full">
                  <span className="w-2 h-2 rounded-full bg-white animate-pulse" />
                  Live Now
                </span>
              ) : (
                <span className="inline-flex items-center gap-2 bg-background-800 text-white text-xs font-label font-semibold tracking-widest uppercase px-4 py-2 rounded-full">
                  <i className="ri-time-line" aria-hidden="true" />
                  Offline
                </span>
              )}
            </div>
            <h1 className="font-heading text-4xl md:text-5xl font-bold text-white leading-tight mb-4">
              {isLive ? live?.title || 'TGMCI Live Service' : 'Watch Our Live Services'}
            </h1>
            <div className="w-12 h-1 bg-accent-500 mx-auto mb-6" />
            <p className="text-white/60 text-base leading-relaxed">
              {isLive
                ? live?.description || 'Join us now for our live worship service.'
                : `We are not streaming right now. Join us for our next service at ${live?.next_service_time || 'Sunday 10:00 AM'}.`}
            </p>
          </div>
        </section>

        {/* Main content */}
        <section className="py-16 bg-background-50">
          <div className="max-w-[1100px] mx-auto px-4 md:px-8">
            {loading ? (
              <p className="text-center text-foreground-400 py-16">
                <i className="ri-loader-4-line animate-spin inline-block" aria-hidden="true" /> Loading...
              </p>
            ) : isLive ? (
              <div className="space-y-10">
                {/* Player */}
                {live?.embed_url ? (
                  <div className="aspect-video bg-foreground-950 rounded-2xl overflow-hidden">
                    <iframe
                      src={live.embed_url}
                      title={live.title || 'Live stream'}
                      className="w-full h-full"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                    />
                  </div>
                ) : (
                  <div className="aspect-video bg-primary-100 rounded-2xl flex items-center justify-center text-foreground-400">
                    <div className="text-center">
                      <i className="ri-live-line text-5xl text-primary-400" aria-hidden="true" />
                      <p className="mt-3 text-sm">Stream URL not configured yet.</p>
                    </div>
                  </div>
                )}

                {/* Prayer Request (only when live) */}
                <div className="max-w-xl mx-auto">
                  <div className="text-center mb-6">
                    <p className="text-accent-600 font-label text-sm tracking-widest uppercase font-semibold mb-3">
                      We're Here to Pray With You
                    </p>
                    <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900">
                      Submit a Prayer Request
                    </h2>
                    <div className="w-12 h-1 bg-accent-500 mt-5 mx-auto" />
                  </div>

                  {status === 'success' ? (
                    <div className="text-center p-8 bg-green-50 border border-green-200 rounded-2xl">
                      <div className="w-14 h-14 flex items-center justify-center rounded-full bg-green-100 text-green-600 mx-auto mb-4">
                        <i className="ri-check-line text-2xl" aria-hidden="true" />
                      </div>
                      <p className="text-green-700 text-sm">{msg}</p>
                      <button type="button" onClick={() => setStatus('idle')} className="btn-primary mt-5">
                        Submit Another Request
                      </button>
                    </div>
                  ) : (
                    <form onSubmit={handlePrayer} className="bg-background-100 rounded-2xl border border-background-200 p-7 space-y-4">
                      <div style={{ position: 'absolute', left: '-9999px' }}>
                        <input type="text" name="website_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" value={honeypot} onChange={(e) => setHoneypot(e.target.value)} />
                      </div>

                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={anonymous}
                          onChange={(e) => setAnonymous(e.target.checked)}
                          className="w-4 h-4 accent-primary-500"
                        />
                        <span className="text-sm text-foreground-600">Submit anonymously</span>
                      </label>

                      {!anonymous && (
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          <input
                            type="text"
                            name="name"
                            placeholder="Your name (optional)"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            className="px-4 py-3 border border-background-300 rounded-lg text-sm bg-background-50 focus:outline-none focus:ring-2 focus:ring-primary-300"
                          />
                          <input
                            type="email"
                            name="email"
                            placeholder="Email (optional)"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            className="px-4 py-3 border border-background-300 rounded-lg text-sm bg-background-50 focus:outline-none focus:ring-2 focus:ring-primary-300"
                          />
                        </div>
                      )}

                      <div>
                        <textarea
                          name="request"
                          rows={5}
                          required
                          placeholder="Share your prayer request..."
                          value={request}
                          onChange={(e) => setRequest(e.target.value.slice(0, MAX_LENGTH))}
                          className="w-full px-4 py-3 border border-background-300 rounded-lg text-sm bg-background-50 focus:outline-none focus:ring-2 focus:ring-primary-300 resize-none"
                        />
                        <p className={`text-xs mt-1 text-right ${request.length >= MAX_LENGTH ? 'text-red-500' : 'text-foreground-400'}`}>
                          {request.length}/{MAX_LENGTH}
                        </p>
                      </div>

                      {status === 'error' && (
                        <p className="text-red-600 text-sm bg-red-50 border border-red-200 rounded-lg p-3">{msg}</p>
                      )}

                      <button type="submit" disabled={status === 'loading'} className="btn-primary w-full justify-center py-3.5">
                        {status === 'loading' ? 'Sending...' : <><i className="ri-hand-heart-line" aria-hidden="true" /> Submit Prayer Request</>}
                      </button>
                    </form>
                  )}
                </div>
              </div>
            ) : (
              <div className="text-center py-16">
                <div className="w-20 h-20 mx-auto flex items-center justify-center rounded-full bg-primary-50 text-primary-600 mb-6">
                  <i className="ri-time-line text-4xl" aria-hidden="true" />
                </div>
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-3">
                  We'll Be Live Soon
                </h2>
                <p className="text-foreground-500 text-base max-w-md mx-auto">
                  Our next service is {live?.next_service_time || 'Sunday at 10:00 AM'}. Join us here when we go live — you can also submit a prayer request during the stream.
                </p>
                <a href="/contact" className="btn-primary mt-8">
                  <i className="ri-mail-line" aria-hidden="true" />
                  Contact Us
                </a>
              </div>
            )}
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}